/**
 * Platform Haptics Abstraction
 */

import { bridge, BridgeChannels, BridgeActions } from '../bridge';

export type HapticImpactStyle = 'light' | 'medium' | 'heavy';
export type HapticNotificationStyle = 'success' | 'warning' | 'error';

export interface HapticsPlatform {
  /** Trigger impact haptic */
  impact(style?: HapticImpactStyle): void;
  
  /** Trigger notification haptic */
  notification(style?: HapticNotificationStyle): void;
  
  /** Trigger selection haptic */
  selection(): void;
  
  /** Check if haptics are supported */
  isSupported(): boolean;
}

class WebHaptics implements HapticsPlatform {
  impact(_style?: HapticImpactStyle): void {
    // Web Vibration API is very limited, only supports duration
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  }

  notification(_style?: HapticNotificationStyle): void {
    if ('vibrate' in navigator) {
      navigator.vibrate([10, 50, 10]);
    }
  }

  selection(): void {
    if ('vibrate' in navigator) {
      navigator.vibrate(5);
    }
  }

  isSupported(): boolean {
    return 'vibrate' in navigator;
  }
}

class NativeHaptics implements HapticsPlatform {
  impact(style: HapticImpactStyle = 'medium'): void {
    bridge.emit(BridgeChannels.HAPTICS, BridgeActions.IMPACT, { style });
  }

  notification(style: HapticNotificationStyle = 'success'): void {
    bridge.emit(BridgeChannels.HAPTICS, 'notification', { style });
  }

  selection(): void {
    bridge.emit(BridgeChannels.HAPTICS, BridgeActions.SELECTION);
  }

  isSupported(): boolean {
    return true;
  }
}

const webHaptics = new WebHaptics();
const nativeHaptics = new NativeHaptics();

export function getHapticsPlatform(): HapticsPlatform {
  return bridge.isNative ? nativeHaptics : webHaptics;
}

// Convenience functions for common haptics
export const haptics = {
  light: () => getHapticsPlatform().impact('light'),
  medium: () => getHapticsPlatform().impact('medium'),
  heavy: () => getHapticsPlatform().impact('heavy'),
  success: () => getHapticsPlatform().notification('success'),
  warning: () => getHapticsPlatform().notification('warning'),
  error: () => getHapticsPlatform().notification('error'),
  selection: () => getHapticsPlatform().selection(),
};

